import { Component } from '@theme/component';

/**
 * Decodes HTML entities from escaped data attributes into readable text.
 * @param {string} value
 * @returns {string}
 */
const decodeHtmlEntities = (value) => {
  if (!value || !value.includes('&')) return value;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

/**
 * @typedef {Object} VariantData
 * @property {number} id
 * @property {boolean} available
 * @property {number} inventory_quantity
 * @property {string | null} inventory_management
 * @property {string} inventory_policy
 * @property {string[]} options
 * @property {string} [title]
 * @property {string} [image_url]
 * @property {string} [sku]
 * @property {number} [price]
 * @property {number | null} [compare_at_price]
 * @property {string} [preorder_message]
 * @property {string} [preorder_property_message]
 * @property {string} [preorder_raw_date]
 */

/**
 * Mini PDP Variant Selector
 *
 * A variant selector that updates the form's variant ID without fetching new content.
 * Handles size selection, notify-me mode switching, and low stock messaging.
 */
class MiniPDPVariantSelector extends Component {
  /** @type {Map<string, VariantData>} */
  #variantMap = new Map();

  /** @type {HTMLInputElement | null} */
  #variantIdInput = null;

  /** @type {HTMLButtonElement | null} */
  #addToCartButton = null;

  /** @type {HTMLElement | null} */
  #buttonTextContainer = null;

  /** @type {HTMLElement | null} */
  #lowStockContainer = null;

  /** @type {number} */
  #lowStockThreshold = 5;

  /** @type {string} */
  #lowStockMessage = 'Low Stock';

  /** @type {HTMLElement | null} */
  #miniPdpContainer = null;

  /** @type {string} */
  #notifyMeText = 'Notify Me';

  /** @type {string} */
  #addToCartText = 'Add to Cart';

  /** @type {string} */
  #unavailableText = 'Unavailable';

  /** @type {boolean} */
  #isInNotifyMeMode = false;

  /** @type {boolean} */
  #isInternationalRestricted = false;

  /** @type {HTMLInputElement | null} */
  #skuLineItemInput = null;

  /** @type {((ev: Event) => void) | null} */
  #boundOpenNotifyMeDrawer = null;

  connectedCallback() {
    super.connectedCallback();

    // Parse variant data
    const script = this.querySelector('script[type="application/json"]');
    if (script) {
      try {
        const variants = JSON.parse(script.textContent || '[]');
        this.#buildVariantMap(variants);
      } catch (e) {
        console.warn('Mini PDP: Failed to parse variants', e);
      }
    }

    // Get settings from data attributes
    this.#lowStockThreshold = parseInt(this.dataset.lowStockThreshold || '5', 10);
    this.#lowStockMessage = decodeHtmlEntities(this.dataset.lowStockMessage || 'Low Stock');
    this.#notifyMeText = decodeHtmlEntities(this.dataset.notifyMeText || 'Notify Me');
    this.#addToCartText = decodeHtmlEntities(this.dataset.addToCartText || 'Add to Cart');
    this.#unavailableText = decodeHtmlEntities(this.dataset.unavailableText || 'Unavailable');
    this.#isInternationalRestricted = this.dataset.internationalRestricted === 'true';

    // Find the form elements
    const formId = this.dataset.formId;
    if (formId) {
      const scopedRoot =
        this.closest('.mini-pdp') || this.closest('.mini-pdp__product-content') || this;
      const escapedFormId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(formId) : formId;
      const form = scopedRoot.querySelector(`#${escapedFormId}`) || document.getElementById(formId);
      this.#variantIdInput = form?.querySelector('input[name="id"]') ?? null;
      this.#addToCartButton =
        form?.querySelector('button[type="submit"], button[type="button"]') ?? null;
      this.#buttonTextContainer =
        this.#addToCartButton?.querySelector('.add-to-cart-text__content-inner') ?? null;
      this.#skuLineItemInput =
        form?.querySelector('input[data-sku-suffix], input[data-sku-template]') ?? null;
    }

    // Find the low stock container
    this.#lowStockContainer = this.querySelector('.mini-pdp__low-stock') ?? null;
    this.#miniPdpContainer = this.closest('.mini-pdp');

    // Create bound function for event listener management
    this.#boundOpenNotifyMeDrawer = this.#openNotifyMeDrawer.bind(this);

    // Listen for size changes
    this.addEventListener('change', this.#handleChange.bind(this));

    // Initialize state for the current variant
    this.#initializeCurrentVariantState();
  }

  /**
   * Initialize state based on the currently selected variant
   * This handles the case where the page loads with an unavailable variant selected
   */
  #initializeCurrentVariantState() {
    const sizeFieldset = this.querySelector('.mini-pdp__variants-fieldset');

    // If the size fieldset exists but has no checked radio, the user must pick a size
    // before we initialize button state (matches main PDP "no default size" behavior).
    if (sizeFieldset && !sizeFieldset.querySelector('input[type="radio"]:checked')) {
      return;
    }

    const selectedOptions = this.#getSelectedOptions();

    let variant;
    if (selectedOptions.length > 0) {
      const key = selectedOptions.join('/');
      variant = this.#variantMap.get(key);
    }

    // Fallback: when no radios are checked (e.g. all variants sold out), look up by the
    // variant ID already rendered into the form's hidden input.
    if (!variant && this.#variantIdInput?.value) {
      const formVariantId = Number(this.#variantIdInput.value);
      for (const v of this.#variantMap.values()) {
        if (v.id === formVariantId) {
          variant = v;
          break;
        }
      }
    }

    if (!variant) return;

    const shouldBeNotifyMe = !variant.available && variant.inventory_management === 'shopify';

    const checkedInput = sizeFieldset?.querySelector('input[type="radio"]:checked');
    let selectedSize = checkedInput instanceof HTMLInputElement ? checkedInput.value : '';

    if (!selectedSize && sizeFieldset && variant.options) {
      const sizeOptionIndex = parseInt(sizeFieldset.getAttribute('data-option-index') || '0', 10);
      selectedSize = variant.options[sizeOptionIndex] ?? '';
    }

    this.#updateButtonState(variant, shouldBeNotifyMe);
    this.#updateNotifyMeDrawer(variant.id, selectedSize);
    this.#updateAddToCartComponentVariant(variant, selectedSize);
    this.#updateLowStockMessage(variant);
    this.#updateCnstrcVariationId(variant.id);
    this.#updatePreorderLineItemProperties(variant);
  }

  /**
   * Build a map of option combinations to variant data
   * @param {Array<{id: number, available: boolean, options: string[], title?: string, image_url?: string, inventory_quantity: number, inventory_management: string | null, inventory_policy: string, sku?: string, price?: number, compare_at_price?: number | null, preorder_message?: string, preorder_property_message?: string, preorder_raw_date?: string}>} variants
   */
  #buildVariantMap(variants) {
    for (const variant of variants) {
      // Create a key from all options (e.g., "Indigo/29")
      const key = variant.options.join('/');
      this.#variantMap.set(key, {
        id: variant.id,
        available: variant.available,
        inventory_quantity: variant.inventory_quantity,
        inventory_management: variant.inventory_management,
        inventory_policy: variant.inventory_policy,
        options: variant.options,
        title: variant.title ?? '',
        image_url: variant.image_url ?? '',
        sku: variant.sku ?? '',
        price: variant.price,
        compare_at_price: variant.compare_at_price,
        preorder_message: variant.preorder_message ?? '',
        preorder_property_message: variant.preorder_property_message ?? '',
        preorder_raw_date: variant.preorder_raw_date ?? '',
      });
    }
  }

  /**
   * Handle size option change
   * @param {Event} event
   */
  #handleChange(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.type !== 'radio') return;

    // Get all selected options
    const selectedOptions = this.#getSelectedOptions();
    const key = selectedOptions.join('/');
    const selectedSize = event.target.value;

    // Find the matching variant
    const variant = this.#variantMap.get(key);

    if (variant && this.#variantIdInput && !this.#isInternationalRestricted) {
      // Update the form's variant ID
      this.#variantIdInput.value = String(variant.id);

      this.#updateCnstrcVariationId(variant.id);

      // Determine if this is a notify-me situation
      // If variant is unavailable and inventory is managed by Shopify, show notify-me
      const shouldBeNotifyMe = !variant.available && variant.inventory_management === 'shopify';

      // Update button state
      this.#updateButtonState(variant, shouldBeNotifyMe);

      // Update notify-me drawer
      this.#updateNotifyMeDrawer(variant.id, selectedSize);

      // Update add-to-cart component so ATC success popup shows the correct variant
      this.#updateAddToCartComponentVariant(variant, selectedSize);

      // Update low stock message
      this.#updateLowStockMessage(variant);

      // Update variant ID input disabled state
      this.#variantIdInput.disabled = !variant.available;

      // Update SKU-prefixed line item property value
      this.#updateSkuLineItemProperty(variant);
      this.#updatePreorderLineItemProperties(variant);
    }

    // Hide "Please select a size" error once a size is chosen
    const section = this.closest(
      '.mini-pdp__product-content, .shopify-section, dialog, product-card',
    );
    const sizeError = section?.querySelector('.size-select-error');
    if (sizeError) {
      sizeError.classList.add('hidden');
    }

    // Update visual selection state
    this.#updateSelectedState(event.target);
  }

  /**
   * Update the add-to-cart button state based on variant availability
   * @param {VariantData} variant
   * @param {boolean} shouldBeNotifyMe
   */
  #updateButtonState(variant, shouldBeNotifyMe) {
    if (!this.#addToCartButton) return;

    // Internationally restricted products keep the disabled button and restriction message
    // rendered by Liquid. That message outranks preorder text, and no variant is orderable,
    // so there is no state here worth recomputing.
    if (this.#isInternationalRestricted) return;

    if (shouldBeNotifyMe) {
      // Switch to notify-me mode
      if (!this.#isInNotifyMeMode) {
        this.#addToCartButton.type = 'button';
        this.#addToCartButton.disabled = false;
        const openNotifyMe = this.#boundOpenNotifyMeDrawer;
        if (openNotifyMe) {
          this.#addToCartButton.addEventListener('click', openNotifyMe);
        }
        const atcComponent = this.#addToCartButton.closest('add-to-cart-component');
        if (atcComponent instanceof HTMLElement) atcComponent.dataset.notifyMeMode = 'true';
        this.#isInNotifyMeMode = true;
      }

      // Update button text with icon
      if (this.#buttonTextContainer) {
        this.#buttonTextContainer.innerHTML = `
          <span class="svg-wrapper add-to-cart-icon">
            <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 3.5L9 8.5L17 3.5M2 1H16C16.5523 1 17 1.44772 17 2V12C17 12.5523 16.5523 13 16 13H2C1.44772 13 1 12.5523 1 12V2C1 1.44772 1.44772 1 2 1Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          ${this.#notifyMeText}
        `;
      }
    } else {
      // Switch to add-to-cart mode
      if (this.#isInNotifyMeMode) {
        this.#addToCartButton.type = 'submit';
        const openNotifyMe = this.#boundOpenNotifyMeDrawer;
        if (openNotifyMe) {
          this.#addToCartButton.removeEventListener('click', openNotifyMe);
        }
        const atcComponent = this.#addToCartButton.closest('add-to-cart-component');
        if (atcComponent instanceof HTMLElement) delete atcComponent.dataset.notifyMeMode;
        this.#isInNotifyMeMode = false;
      }

      // Update button text (no icon)
      if (this.#buttonTextContainer) {
        if (variant.available && variant.preorder_message) {
          this.#buttonTextContainer.textContent = variant.preorder_message;
        } else if (variant.available) {
          this.#buttonTextContainer.textContent = this.#addToCartText;
        } else {
          this.#buttonTextContainer.textContent = this.#unavailableText;
        }
      }

      // Update disabled state based on availability
      this.#addToCartButton.disabled = !variant.available;
    }
  }

  /**
   * Open the global notify-me drawer with the current variant data
   * @param {Event} event
   */
  #openNotifyMeDrawer(event) {
    event.preventDefault();

    const selectedOptions = this.#getSelectedOptions();
    const key = selectedOptions.join('/');
    const variant = this.#variantMap.get(key);
    if (!variant) return;

    // Only stop propagation after confirming we have a valid variant.
    // If the variant isn't found (e.g. single-size products where the Size
    // fieldset is omitted), let the event bubble so the document-level
    // [data-open-notify-me] handler can open the drawer instead.
    event.stopPropagation();

    const sizeFieldset = this.querySelector('.mini-pdp__variants-fieldset');
    const checkedInput = sizeFieldset?.querySelector('input[type="radio"]:checked');
    const sizeLabel = checkedInput instanceof HTMLInputElement ? checkedInput.value : '';

    window.openNotifyMeDrawer?.(String(variant.id), sizeLabel, {
      productId: this.dataset.productId || undefined,
      productTitle: this.dataset.productTitle || undefined,
      productType: this.dataset.productType || undefined,
      variantSku: variant.sku || undefined,
      variantTitle: variant.title || undefined,
      variantPrice: variant.price,
      variantCompareAtPrice: variant.compare_at_price ?? undefined,
    });
  }

  /**
   * Update the notify-me drawer with the current variant info.
   * No-op: the global drawer is populated when opened via #openNotifyMeDrawer.
   * @param {number} _variantId
   * @param {string} _sizeLabel
   */
  #updateNotifyMeDrawer(_variantId, _sizeLabel) {
    // Global drawer is populated on open
  }

  /**
   * Update the add-to-cart component's variant data so the ATC success popup shows the correct variant.
   * @param {VariantData} variant
   * @param {string} selectedSize
   */
  #updateAddToCartComponentVariant(variant, selectedSize) {
    const addToCartComponent = this.closest('.mini-pdp')?.querySelector('add-to-cart-component');
    if (!addToCartComponent) return;

    addToCartComponent.setAttribute('data-variant-id', String(variant.id));
    addToCartComponent.setAttribute('data-product-variant-title', variant.title ?? '');
    addToCartComponent.setAttribute(
      'data-product-variant-size',
      selectedSize || variant.title || '',
    );
    if (variant.image_url) {
      addToCartComponent.setAttribute('data-product-variant-media', variant.image_url);
    }
  }

  /**
   * Update the low stock message visibility
   * @param {VariantData} variant
   */
  #updateLowStockMessage(variant) {
    if (!this.#lowStockContainer) return;

    // Show low stock message if:
    // - Variant is available
    // - Inventory quantity is greater than 0
    // - Inventory quantity is at or below threshold
    const shouldShowLowStock =
      variant.available &&
      variant.inventory_quantity > 0 &&
      variant.inventory_quantity <= this.#lowStockThreshold;

    if (shouldShowLowStock) {
      // Create or update the low stock message
      let messageElement = this.#lowStockContainer.querySelector('.low-stock-message');
      if (!messageElement) {
        messageElement = document.createElement('span');
        messageElement.className = 'low-stock-message low-stock-message--mini-pdp';
        messageElement.setAttribute('role', 'status');
        messageElement.setAttribute('aria-live', 'polite');
        this.#lowStockContainer.appendChild(messageElement);
      }
      messageElement.textContent = this.#lowStockMessage;
      this.#lowStockContainer.style.display = '';
    } else {
      // Hide the container
      this.#lowStockContainer.style.display = 'none';
    }
  }

  /** Get all currently selected option values in order. @returns {string[]} */
  #getSelectedOptions() {
    const options = [];
    const fieldsets = this.querySelectorAll('fieldset[data-option-index], div[data-option-index]');

    // Sort by option index to maintain order
    const sortedFieldsets = Array.from(fieldsets).sort((a, b) => {
      const indexA = parseInt(a.getAttribute('data-option-index') || '0', 10);
      const indexB = parseInt(b.getAttribute('data-option-index') || '0', 10);
      return indexA - indexB;
    });

    for (const fieldset of sortedFieldsets) {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked instanceof HTMLInputElement) {
        options.push(checked.value);
      } else {
        // Fallback: after template cloning the :checked pseudo-class may not
        // match radios that only carry the HTML checked attribute. Look for a
        // sibling hidden <input> with the same data-option-index.
        const idx = fieldset.getAttribute('data-option-index');
        const hiddenInput = this.querySelector(`input[type="hidden"][data-option-index="${idx}"]`);
        if (hiddenInput instanceof HTMLInputElement && hiddenInput.value) {
          options.push(hiddenInput.value);
        }
      }
    }

    return options;
  }

  /**
   * Update the SKU-based line item property hidden input with the new variant's SKU.
   * Supports either a `data-sku-suffix` mode (`sku + suffix`) or a
   * `data-sku-template` mode (replace every `{{sku}}` token).
   * @param {VariantData} variant
   */
  #updateSkuLineItemProperty(variant) {
    if (!this.#skuLineItemInput) return;
    const sku = variant.sku ?? '';
    const template = this.#skuLineItemInput.dataset.skuTemplate;
    if (template) {
      this.#skuLineItemInput.value = template.split('{{sku}}').join(sku);
      return;
    }
    const suffix = this.#skuLineItemInput.dataset.skuSuffix ?? '';
    this.#skuLineItemInput.value = sku + suffix;
  }

  /**
   * Sync preorder line item properties with currently selected variant.
   * @param {VariantData} variant
   */
  #updatePreorderLineItemProperties(variant) {
    const form = this.#variantIdInput?.form;
    if (!form) return;

    const preorderDateInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="properties[_preOrderDate]"]')
    );
    const preorderMessageInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="properties[Preorder]"]')
    );

    const preorderPropertyMessage =
      variant.preorder_property_message ?? variant.preorder_message ?? '';
    const hasPreorderData = Boolean(variant.preorder_raw_date && preorderPropertyMessage);
    if (!hasPreorderData) {
      preorderDateInput?.remove();
      preorderMessageInput?.remove();
      return;
    }

    const dateInput = preorderDateInput || document.createElement('input');
    dateInput.type = 'hidden';
    dateInput.name = 'properties[_preOrderDate]';
    dateInput.value = variant.preorder_raw_date ?? '';

    const messageInput = preorderMessageInput || document.createElement('input');
    messageInput.type = 'hidden';
    messageInput.name = 'properties[Preorder]';
    messageInput.value = preorderPropertyMessage;

    if (!preorderDateInput) {
      form.appendChild(dateInput);
    }
    if (!preorderMessageInput) {
      form.appendChild(messageInput);
    }
  }

  /**
   * Keep Constructor variation tracking attribute in sync with selected variant.
   * @param {number} variantId
   */
  #updateCnstrcVariationId(variantId) {
    if (this.#miniPdpContainer) {
      this.#miniPdpContainer.dataset.cnstrcItemVariationId = String(variantId);
      return;
    }
    const container = this.closest('.mini-pdp');
    if (container instanceof HTMLElement) {
      container.dataset.cnstrcItemVariationId = String(variantId);
      this.#miniPdpContainer = container;
    }
  }

  /**
   * Update the visual selected state of size options
   * @param {HTMLInputElement} selectedInput
   */
  #updateSelectedState(selectedInput) {
    const fieldset = selectedInput.closest('.mini-pdp__variants-fieldset');
    if (!fieldset) return;

    const allRadios = fieldset.querySelectorAll('input[type="radio"]');
    for (const radio of allRadios) {
      const wasCurrent = radio.getAttribute('data-current-checked') === 'true';
      radio.setAttribute('data-previous-checked', wasCurrent ? 'true' : 'false');
      radio.setAttribute('data-current-checked', radio === selectedInput ? 'true' : 'false');
    }

    const labels = fieldset.querySelectorAll('label');
    for (const label of labels) {
      label.classList.remove('is-selected');
    }

    const selectedLabel = selectedInput.closest('label');
    if (selectedLabel) {
      selectedLabel.classList.add('is-selected');
    }

    const selectedValueDisplay = fieldset.querySelector(
      '.mini-pdp__variants-legend .variant-option__swatch-value',
    );
    if (selectedValueDisplay) {
      selectedValueDisplay.textContent = `— ${selectedInput.value}`;
    }
  }
}

if (!customElements.get('mini-pdp-variant-selector')) {
  customElements.define('mini-pdp-variant-selector', MiniPDPVariantSelector);
}
